import {
  AggregateSessionScope,
  DEFAULT_DATE_FORMAT_FNS,
  GroupAggregateSortBy,
  GroupValidity,
  useGetAttendanceCodesQuery,
  useGetGroupAttendanceAggregateInfiniteQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { toStudentRows } from '../common/attendanceGridData';
import {
  buildSubjectGroupColumns,
  buildSubjectGroupRows,
  buildSubjectGroupSkeleton,
  selectMembersForColumns,
} from '../common/buildSubjectGroupGridFromAggregate';
import { GroupGridMember } from '../common/buildGroupGrid';
import {
  AGGREGATE_INCLUDE_ALL_POINTS,
  ATTENDANCE_SUBJECT_VISIBLE_COLUMNS as VISIBLE_COLUMNS,
} from '../common/constants';
import { useColumnWindow } from './useColumnWindow';

export const SUBJECT_REGISTERS_PAGE_SIZE = 2 * VISIBLE_COLUMNS;

export const useAttendanceGridForSubjectGroup = (
  groupId: string,
  validity: GroupValidity,
  members: GroupGridMember[],
) => {
  const { schoolId = '' } = useAuth();
  const dateFrom = validity.date_from ?? validity.from_school_year?.start ?? '';
  const dateTo = format(new Date(), DEFAULT_DATE_FORMAT_FNS);
  const enabled = !!groupId && !!schoolId && !!dateFrom;

  const { data: codes = [] } = useGetAttendanceCodesQuery(schoolId, { enabled: !!schoolId });

  const { data, fetchNextPage, hasNextPage, isInitialLoading } =
    useGetGroupAttendanceAggregateInfiniteQuery(
      {
        groupId,
        school_id: schoolId,
        date_from: dateFrom,
        date_to: dateTo,
        sort_by: GroupAggregateSortBy.RegisterDateDesc,
        page_size: SUBJECT_REGISTERS_PAGE_SIZE,
        session_scope: AggregateSessionScope.Group,
        include: AGGREGATE_INCLUDE_ALL_POINTS,
      },
      { enabled, refetchOnMount: 'always' },
    );

  const allColumns = useMemo(() => (data ? buildSubjectGroupColumns(data.pages) : []), [data]);

  const {
    columns: visibleColumns,
    isAwaitingOlder,
    showOlder,
    showNewer,
  } = useColumnWindow(allColumns, VISIBLE_COLUMNS, hasNextPage ? fetchNextPage : undefined);

  const { columns, rows } = useMemo(() => {
    if (!members.length) return { columns: [], rows: [] };

    if (!data || isAwaitingOlder) {
      const shownMembers = data ? selectMembersForColumns(members, visibleColumns) : members;
      const skeleton = buildSubjectGroupSkeleton(shownMembers, VISIBLE_COLUMNS);
      return { columns: skeleton.columns, rows: toStudentRows(skeleton.rows) };
    }

    const rows = buildSubjectGroupRows({
      members,
      pages: data.pages,
      columns: visibleColumns,
    });

    return { columns: visibleColumns, rows: toStudentRows(rows) };
  }, [members, data, visibleColumns, isAwaitingOlder]);

  return {
    columns,
    rows,
    codes,
    showNewerColumns: showNewer,
    showOlderColumns: showOlder,
    isTableLoading: isInitialLoading,
  };
};
